import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Sparkle } from '../components/ui/Sparkle';
import { Bookmark, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookmarksPage() {
    const { getAuthHeaders } = useAuth();
    const [bookmarks, setBookmarks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBookmarks = async () => {
            try {
                const response = await axios.get(`${API}/bookmarks`, {
                    headers: getAuthHeaders()
                });
                setBookmarks(response.data);
            } catch (error) {
                console.error('Failed to fetch bookmarks:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchBookmarks();
    }, [getAuthHeaders]);

    const removeBookmark = async (bookmarkId) => {
        try {
            await axios.delete(`${API}/bookmarks/${bookmarkId}`, {
                headers: getAuthHeaders()
            });
            setBookmarks(bookmarks.filter(b => b.id !== bookmarkId));
            toast.success('Záložka byla odebrána');
        } catch (error) {
            toast.error('Nepodařilo se odebrat záložku');
        }
    };

    return (
        <div className="min-h-screen" data-testid="bookmarks-page">
            <section className="listarna-section">
                <div className="listarna-container">
                    <div className="max-w-3xl mx-auto">
                        {/* Header */}
                        <div className="mb-12">
                            <div className="flex items-center gap-2 mb-4">
                                <Bookmark className="w-6 h-6 text-amber-400" />
                            </div>
                            <h1 className="font-heading text-3xl md:text-4xl font-semibold mb-2">
                                Uložené povídky
                            </h1>
                            <p className="text-muted-foreground">
                                Vaše oblíbené povídky na jednom místě
                            </p>
                        </div>

                        {/* Bookmarks List */}
                        {loading ? (
                            <div className="space-y-4">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="animate-pulse flex gap-4 p-4 bg-secondary/30 rounded-lg">
                                        <div className="w-20 h-28 bg-secondary rounded" />
                                        <div className="flex-1">
                                            <div className="h-6 bg-secondary rounded w-3/4 mb-2" />
                                            <div className="h-4 bg-secondary rounded w-1/2" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : bookmarks.length === 0 ? (
                            <div className="text-center py-16">
                                <Sparkle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                                <h2 className="font-heading text-xl mb-2">Zatím nemáte uložené povídky</h2>
                                <p className="text-muted-foreground mb-6">
                                    Procházejte povídky a ukládejte si své oblíbené
                                </p>
                                <Link to="/povidky">
                                    <Button className="listarna-btn-primary">
                                        Prohlédnout povídky
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {bookmarks.map((bookmark, index) => (
                                    <div 
                                        key={bookmark.id}
                                        className="flex gap-4 p-4 listarna-card rounded-lg animate-fade-in"
                                        style={{ animationDelay: `${index * 0.1}s` }}
                                        data-testid={`bookmark-${bookmark.story_id}`}
                                    >
                                        <Link to={`/povidka/${bookmark.story_id}`}>
                                            {bookmark.story_cover ? (
                                                <img 
                                                    src={bookmark.story_cover} 
                                                    alt={bookmark.story_title}
                                                    className="w-20 h-28 object-cover rounded"
                                                />
                                            ) : (
                                                <div className="w-20 h-28 bg-secondary rounded flex items-center justify-center">
                                                    <Bookmark className="w-6 h-6 text-muted-foreground" />
                                                </div>
                                            )}
                                        </Link>
                                        <div className="flex-1 min-w-0">
                                            <Link to={`/povidka/${bookmark.story_id}`}>
                                                <h3 className="font-heading text-lg font-medium hover:underline truncate">
                                                    {bookmark.story_title}
                                                </h3>
                                            </Link>
                                            <p className="text-sm text-muted-foreground mb-2">
                                                {bookmark.story_author}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                Uloženo {new Date(bookmark.created_at).toLocaleDateString('cs-CZ')}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => removeBookmark(bookmark.id)}
                                            className="p-2 hover:bg-destructive/10 hover:text-destructive rounded-full transition-colors"
                                            data-testid={`remove-bookmark-${bookmark.story_id}`}
                                            aria-label="Odebrat záložku"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
